import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Copy, MapPin, Plus, Send, Users, XCircle } from "lucide-react";
import {
  getPartnership, updatePartnership, assignContractorSites, setContractorSiteActive,
  publishContractorShift, cancelContractorShift, resendContractorInvite, revokeContractorInvite,
  buildContractorPayPeriod, setPayPeriodStatus, getContractorScope,
} from "@/lib/contractors.functions";
import {
  PARTNERSHIP_STATUS_LABEL, PAY_PERIOD_STATUS_LABEL, contractedShiftStatus, shiftHours,
  canManageContractorPartners, type PartnershipStatus, type PayPeriodStatus,
} from "@/lib/contractors";
import { ShiftStatusBadge } from "@/components/contractors/ShiftStatusBadge";
import { ContractorWorkflowStrip } from "@/components/contractors/ContractorWorkflowStrip";
import { ContractorDisclaimer } from "@/components/contractors/ContractorDisclaimer";

export const Route = createFileRoute("/_authenticated/contractors/$partnershipId")({
  component: PartnershipPage,
  head: () => ({
    meta: [
      { title: "Contractor relationship — MissionReady" },
      { name: "description", content: "Manage a contractor partner relationship: sites, members, contracted shifts and pay periods." },
      { property: "og:title", content: "Contractor relationship — MissionReady" },
      { property: "og:description", content: "Sites, members, contracted shifts and timesheets for one contractor partner." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

function fmt(dt: string) {
  return new Date(dt).toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

function PartnershipPage() {
  const { partnershipId } = Route.useParams();
  const { currentOrg, currentRole, isPlatformAdmin } = useAuth();
  const orgId = currentOrg?.id ?? "";
  const qc = useQueryClient();
  const allowed = canManageContractorPartners({ role: currentRole, isPlatformAdmin });

  const getFn = useServerFn(getPartnership);
  const scopeFn = useServerFn(getContractorScope);
  const updateFn = useServerFn(updatePartnership);
  const assignFn = useServerFn(assignContractorSites);
  const siteActiveFn = useServerFn(setContractorSiteActive);
  const publishFn = useServerFn(publishContractorShift);
  const cancelFn = useServerFn(cancelContractorShift);
  const resendFn = useServerFn(resendContractorInvite);
  const revokeFn = useServerFn(revokeContractorInvite);
  const buildPeriodFn = useServerFn(buildContractorPayPeriod);
  const periodStatusFn = useServerFn(setPayPeriodStatus);

  const key = ["contractor-partnership", partnershipId];
  const { data, isLoading, error } = useQuery({
    queryKey: key,
    enabled: allowed,
    queryFn: () => getFn({ data: { partnershipId } }),
  });
  const { data: scope } = useQuery({
    queryKey: ["contractor-scope", orgId],
    enabled: Boolean(orgId) && allowed,
    queryFn: () => scopeFn({ data: { orgId } }),
  });
  const { data: allSites } = useQuery({
    queryKey: ["contractor-assignable-sites", orgId],
    enabled: Boolean(orgId) && allowed,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sites").select("id, name").eq("organization_id", orgId).eq("active", true).order("name");
      if (error) throw error;
      return data ?? [];
    },
  });

  const refresh = () => qc.invalidateQueries({ queryKey: key });

  const p: any = data?.partnership;
  const sites: any[] = data?.sites ?? [];
  const members: any[] = data?.members ?? [];
  const invites: any[] = data?.invites ?? [];
  const shifts: any[] = data?.shifts ?? [];
  const payPeriods: any[] = data?.payPeriods ?? [];

  const assignedSiteIds = sites.filter((s) => s.active).map((s) => s.site_id);
  const addable = useMemo(() => {
    const inScope = (allSites ?? []).filter(
      (s: any) => !scope || scope.orgWide || scope.scopedSiteIds.includes(s.id),
    );
    return inScope.filter((s: any) => !assignedSiteIds.includes(s.id));
  }, [allSites, scope, assignedSiteIds.join(",")]);

  const [publishForm, setPublishForm] = useState({
    siteId: "", startsAt: "", endsAt: "", roleLabel: "", notes: "", positions: 1,
  });
  const [periodForm, setPeriodForm] = useState({ start: "", end: "" });
  const [busy, setBusy] = useState<string | null>(null);

  if (!allowed) {
    return (
      <div className="mx-auto max-w-xl px-4 py-10">
        <p className="rounded-md border border-border bg-card p-4 text-sm text-muted-foreground">
          Your role doesn't include managing contractor partner relationships.
        </p>
      </div>
    );
  }

  if (isLoading) return <p className="p-6 text-sm text-muted-foreground">Loading relationship…</p>;
  if (error || !p) {
    return (
      <div className="mx-auto max-w-xl px-4 py-10">
        <p className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          This contractor relationship isn't available to you.
        </p>
        <Link to="/contractors" className="mt-4 inline-block text-sm text-primary hover:underline">
          Back to Contractor Partners
        </Link>
      </div>
    );
  }

  const step =
    payPeriods.length > 0 ? 9
      : shifts.some((s) => s.officer_id) ? 6
        : shifts.length > 0 ? 5
          : assignedSiteIds.length > 0 ? 4
            : members.length > 1 ? 3
              : p.status === "invited" ? 1 : 2;

  const act = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    try { await fn(); await refresh(); }
    catch (err: any) { toast.error(typeof err?.message === "string" ? err.message : "Action failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 pb-24 pt-4 sm:pt-6">
      <Link to="/contractors" className="mb-3 inline-flex min-h-10 items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" aria-hidden /> Contractor Partners
      </Link>

      <header className="mb-4">
        <h1 className="font-display text-2xl font-semibold">{p.contractor_name}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {PARTNERSHIP_STATUS_LABEL[p.status as PartnershipStatus] ?? p.status} ·{" "}
          {p.relationship_type === "ongoing" ? "Ongoing" : "Temporary"}
          {p.starts_on ? ` · ${p.starts_on}` : ""}{p.ends_on ? ` → ${p.ends_on}` : ""}
        </p>
        {p.purpose ? <p className="mt-1 text-sm">{p.purpose}</p> : null}
      </header>

      <ContractorWorkflowStrip currentStep={step} className="mb-5" />

      <Tabs defaultValue="overview">
        <TabsList className="mb-4 grid w-full grid-cols-2 sm:grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="sites">Sites</TabsTrigger>
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="shifts">Shifts</TabsTrigger>
          <TabsTrigger value="timesheets">Timesheets</TabsTrigger>
        </TabsList>

        {/* ------------------------------ Overview ------------------------------ */}
        <TabsContent value="overview" className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-4">
            <h2 className="font-display text-lg font-semibold">Relationship</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="rp-status">Status</Label>
                <Select
                  value={p.status}
                  onValueChange={(v) => act("status", () => updateFn({ data: { partnershipId, status: v as any } }))}
                >
                  <SelectTrigger id="rp-status" className="min-h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(Object.keys(PARTNERSHIP_STATUS_LABEL) as PartnershipStatus[]).map((s) => (
                      <SelectItem key={s} value={s}>{PARTNERSHIP_STATUS_LABEL[s]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rp-type">Duration</Label>
                <Select
                  value={p.relationship_type}
                  onValueChange={(v) => act("type", () => updateFn({ data: { partnershipId, relationshipType: v as any } }))}
                >
                  <SelectTrigger id="rp-type" className="min-h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="temporary">Temporary</SelectItem>
                    <SelectItem value="ongoing">Ongoing</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rp-start">Start date</Label>
                <Input id="rp-start" type="date" className="min-h-11" defaultValue={p.starts_on ?? ""}
                  onBlur={(e) => act("start", () => updateFn({ data: { partnershipId, startsOn: e.target.value || null } }))} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rp-end">End date (access ends, history is kept)</Label>
                <Input id="rp-end" type="date" className="min-h-11" defaultValue={p.ends_on ?? ""}
                  onBlur={(e) => act("end", () => updateFn({ data: { partnershipId, endsOn: e.target.value || null } }))} />
              </div>
            </div>

            <div className="mt-4 flex items-start justify-between gap-3 rounded-lg border border-border bg-muted/20 p-3">
              <div>
                <Label htmlFor="rp-client" className="text-sm font-medium">Share client information</Label>
                <p className="mt-1 text-xs text-muted-foreground">
                  Off by default. When on, only the Contractor Organization Owner can see the client
                  information for the assigned sites — never contractor members.
                </p>
              </div>
              <Switch
                id="rp-client"
                checked={Boolean(p.client_info_access)}
                onCheckedChange={(v) => act("client", () => updateFn({ data: { partnershipId, clientInfoAccess: v } }))}
              />
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-4">
            <h2 className="font-display text-lg font-semibold">Invitations</h2>
            <ul className="mt-3 space-y-2">
              {invites.length === 0 ? (
                <li className="text-sm text-muted-foreground">No invitations recorded.</li>
              ) : invites.map((inv) => {
                const expired = inv.expires_at && new Date(inv.expires_at) < new Date();
                return (
                  <li key={inv.id} className="rounded-lg border border-border p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{inv.email}</p>
                        <p className="text-xs text-muted-foreground">
                          {inv.kind === "member" ? "Contractor member" : inv.kind === "individual" ? "Individual contractor" : "Contractor organization owner"}
                          {" · "}
                          {inv.accepted_at ? "Accepted" : expired ? "Expired" : "Pending"}
                        </p>
                      </div>
                      {!inv.accepted_at ? (
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="min-h-10"
                            onClick={() => {
                              const link = `${window.location.origin}/accept-contract/${inv.token}`;
                              void navigator.clipboard.writeText(link).then(() => toast.success("Invitation link copied"));
                            }}>
                            <Copy className="mr-1 h-3.5 w-3.5" aria-hidden /> Copy link
                          </Button>
                          <Button size="sm" variant="outline" className="min-h-10" disabled={busy === `resend-${inv.id}`}
                            onClick={() => act(`resend-${inv.id}`, async () => {
                              const res: any = await resendFn({ data: { inviteId: inv.id, origin: window.location.origin } });
                              if (res.emailSent) toast.success("Invitation resent"); else toast.warning("Couldn't email — copy the link instead");
                            })}>
                            <Send className="mr-1 h-3.5 w-3.5" aria-hidden /> Resend
                          </Button>
                          <Button size="sm" variant="ghost" className="min-h-10 text-destructive"
                            onClick={() => act(`revoke-${inv.id}`, () => revokeFn({ data: { inviteId: inv.id } }))}>
                            <XCircle className="mr-1 h-3.5 w-3.5" aria-hidden /> Revoke
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          <ContractorDisclaimer />
        </TabsContent>

        {/* -------------------------------- Sites -------------------------------- */}
        <TabsContent value="sites" className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-4">
            <h2 className="font-display text-lg font-semibold">Assigned sites</h2>
            <ul className="mt-3 space-y-2">
              {sites.length === 0 ? (
                <li className="text-sm text-muted-foreground">Not assigned to any site yet.</li>
              ) : sites.map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
                  <div className="min-w-0">
                    <p className="flex items-center gap-1.5 truncate text-sm font-medium">
                      <MapPin className="h-3.5 w-3.5 text-primary" aria-hidden /> {s.sites?.name ?? "Site"}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">{s.sites?.address ?? "—"}</p>
                  </div>
                  <label className="flex items-center gap-2 text-xs">
                    <span className="text-muted-foreground">{s.active ? "Active" : "Paused"}</span>
                    <Switch
                      checked={s.active}
                      aria-label={`Site assignment ${s.sites?.name ?? ""} active`}
                      onCheckedChange={(v) => act(`site-${s.id}`, () => siteActiveFn({ data: { assignmentId: s.id, active: v } }))}
                    />
                  </label>
                </li>
              ))}
            </ul>

            {addable.length > 0 ? (
              <div className="mt-4">
                <Label htmlFor="add-site">Assign another site</Label>
                <div className="mt-1.5 flex gap-2">
                  <Select onValueChange={(v) => act("assign", () => assignFn({ data: { partnershipId, siteIds: [v] } }))}>
                    <SelectTrigger id="add-site" className="min-h-11"><SelectValue placeholder="Choose a site" /></SelectTrigger>
                    <SelectContent>
                      {addable.map((s: any) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Contractors only ever see the sites assigned here, plus the post orders published for them.
                </p>
              </div>
            ) : null}
          </div>
        </TabsContent>

        {/* ------------------------------- Members ------------------------------- */}
        <TabsContent value="members" className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-4">
            <h2 className="flex items-center gap-2 font-display text-lg font-semibold">
              <Users className="h-4 w-4" aria-hidden /> Contractor members
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Managed by the Contractor Organization Owner. They are not your personnel and don't use your seats.
            </p>
            <ul className="mt-3 space-y-2">
              {members.length === 0 ? (
                <li className="text-sm text-muted-foreground">No members yet — the contractor adds their own people.</li>
              ) : members.map((m) => (
                <li key={m.id} className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">
                      {m.profile?.full_name ?? m.profile?.email ?? "Contractor member"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {m.contractor_role === "contractor_owner" ? "Contractor Organization Owner" : "Contractor member"}
                      {m.active ? "" : " · inactive"}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </TabsContent>

        {/* -------------------------------- Shifts ------------------------------- */}
        <TabsContent value="shifts" className="space-y-4">
          <form
            className="rounded-xl border border-border bg-card p-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (!publishForm.siteId || !publishForm.startsAt || !publishForm.endsAt) {
                return toast.error("Pick a site and the shift window");
              }
              void act("publish", async () => {
                await publishFn({
                  data: {
                    partnershipId,
                    siteId: publishForm.siteId,
                    startsAt: new Date(publishForm.startsAt).toISOString(),
                    endsAt: new Date(publishForm.endsAt).toISOString(),
                    roleLabel: publishForm.roleLabel || undefined,
                    notes: publishForm.notes || undefined,
                    positions: publishForm.positions,
                  },
                });
                toast.success("Published to the Shift Claim board");
                setPublishForm({ ...publishForm, startsAt: "", endsAt: "", notes: "" });
              });
            }}
          >
            <h2 className="font-display text-lg font-semibold">Publish contracted shifts</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="ps-site">Site</Label>
                <Select value={publishForm.siteId} onValueChange={(v) => setPublishForm({ ...publishForm, siteId: v })}>
                  <SelectTrigger id="ps-site" className="min-h-11"><SelectValue placeholder="Assigned site" /></SelectTrigger>
                  <SelectContent>
                    {sites.filter((s) => s.active).map((s) => (
                      <SelectItem key={s.site_id} value={s.site_id}>{s.sites?.name ?? "Site"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ps-role">Post / role label</Label>
                <Input id="ps-role" className="min-h-11" placeholder="Overnight patrol"
                  value={publishForm.roleLabel} onChange={(e) => setPublishForm({ ...publishForm, roleLabel: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ps-start">Starts</Label>
                <Input id="ps-start" type="datetime-local" className="min-h-11"
                  value={publishForm.startsAt} onChange={(e) => setPublishForm({ ...publishForm, startsAt: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ps-end">Ends</Label>
                <Input id="ps-end" type="datetime-local" className="min-h-11"
                  value={publishForm.endsAt} onChange={(e) => setPublishForm({ ...publishForm, endsAt: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ps-pos">Positions</Label>
                <Input id="ps-pos" type="number" min={1} max={20} className="min-h-11"
                  value={publishForm.positions}
                  onChange={(e) => setPublishForm({ ...publishForm, positions: Math.max(1, Number(e.target.value) || 1) })} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="ps-notes">Requirements / notes</Label>
                <Textarea id="ps-notes" rows={2} placeholder="Guard card required, radio provided at gate"
                  value={publishForm.notes} onChange={(e) => setPublishForm({ ...publishForm, notes: e.target.value })} />
              </div>
            </div>
            <Button type="submit" className="mt-3 min-h-12 w-full sm:w-auto" disabled={busy === "publish"}>
              <Plus className="mr-2 h-4 w-4" aria-hidden /> Publish to Shift Claim
            </Button>
          </form>

          <div className="rounded-xl border border-border bg-card p-4">
            <h2 className="font-display text-lg font-semibold">Contracted shifts</h2>
            <ul className="mt-3 space-y-2">
              {shifts.length === 0 ? (
                <li className="text-sm text-muted-foreground">Nothing published yet.</li>
              ) : shifts.map((s) => {
                const status = contractedShiftStatus(s);
                return (
                  <li key={s.id} className="rounded-lg border border-border p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{fmt(s.starts_at)} → {fmt(s.ends_at)}</p>
                        <p className="text-xs text-muted-foreground">
                          {s.role_label ?? "Contracted post"} · {shiftHours(s.starts_at, s.ends_at)} h
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <ShiftStatusBadge status={status} />
                        {status !== "cancelled" && status !== "completed" ? (
                          <Button size="sm" variant="ghost" className="min-h-10 text-destructive"
                            onClick={() => act(`cancel-${s.id}`, () => cancelFn({ data: { shiftId: s.id } }))}>
                            Cancel
                          </Button>
                        ) : null}
                      </div>
                    </div>
                    {s.notes ? <p className="mt-1.5 text-xs text-muted-foreground">{s.notes}</p> : null}
                  </li>
                );
              })}
            </ul>
          </div>
        </TabsContent>

        {/* ----------------------------- Timesheets ----------------------------- */}
        <TabsContent value="timesheets" className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-4">
            <h2 className="font-display text-lg font-semibold">Pay periods</h2>
            <div className="mt-3 flex flex-wrap items-end gap-2">
              <div className="space-y-1.5">
                <Label htmlFor="pp-start">From</Label>
                <Input id="pp-start" type="date" className="min-h-11" value={periodForm.start}
                  onChange={(e) => setPeriodForm({ ...periodForm, start: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pp-end">To</Label>
                <Input id="pp-end" type="date" className="min-h-11" value={periodForm.end}
                  onChange={(e) => setPeriodForm({ ...periodForm, end: e.target.value })} />
              </div>
              <Button
                className="min-h-11"
                disabled={busy === "period"}
                onClick={() => {
                  if (!periodForm.start || !periodForm.end) return toast.error("Choose both dates");
                  void act("period", async () => {
                    await buildPeriodFn({ data: { partnershipId, periodStart: periodForm.start, periodEnd: periodForm.end } });
                    toast.success("Pay period built from the time clock records");
                  });
                }}
              >
                Build pay period
              </Button>
            </div>

            <ul className="mt-4 space-y-2">
              {payPeriods.length === 0 ? (
                <li className="text-sm text-muted-foreground">No pay periods yet.</li>
              ) : payPeriods.map((pp) => (
                <li key={pp.id} className="rounded-lg border border-border p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium">{pp.period_start} → {pp.period_end}</p>
                      <p className="text-xs text-muted-foreground">
                        {Number(pp.total_hours).toFixed(2)} h ·{" "}
                        {PAY_PERIOD_STATUS_LABEL[pp.status as PayPeriodStatus] ?? pp.status}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Link to="/contractor/pay-periods" search={{ id: pp.id } as never}>
                        <Button size="sm" variant="outline" className="min-h-10">Review</Button>
                      </Link>
                      {pp.status === "submitted" ? (
                        <Button size="sm" variant="outline" className="min-h-10"
                          onClick={() => act(`recv-${pp.id}`, () => periodStatusFn({ data: { payPeriodId: pp.id, status: "under_review" } }))}>
                          Mark received
                        </Button>
                      ) : null}
                      {pp.status === "under_review" ? (
                        <>
                          <Button size="sm" className="min-h-10"
                            onClick={() => act(`appr-${pp.id}`, () => periodStatusFn({ data: { payPeriodId: pp.id, status: "approved" } }))}>
                            Approve
                          </Button>
                          <Button size="sm" variant="outline" className="min-h-10"
                            onClick={() => act(`ret-${pp.id}`, () => periodStatusFn({ data: { payPeriodId: pp.id, status: "returned" } }))}>
                            Return
                          </Button>
                        </>
                      ) : null}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-muted-foreground">
              Hours come from the existing time clock records for contracted shifts. MissionReady does not
              process payments.
            </p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
